import { Injectable } from "@angular/core";
import { RuleGeneratorService } from "./rule-generator.service";
import { ItemDataService } from "../item-data.service";
import { poe1, poe2 } from "../../../game";
import { JewelleryType, YesNoAny } from "../../models/enums";
import { EquipmentType } from "../../models/equipment-type.enum";
import { Filter, CustomRule, AdvancedConditions, AdvancedEquipmentConditions, ArmourFilter, WeaponFilter } from "../../models/filter-models";
import { Rarity, Comparator } from "../../models/enums";
import { WeaponType, ArmourType, BaseTypeTier, GearBaseTypeMode } from "../../models/enums";
import { itemTypesWithTwoSockets } from "../../data/poe2/item-lists";
import { GenerationHelperService } from "./generation-helper.service";
import { RuleId } from "../../models/rule-id";

@Injectable({
  providedIn: 'root'
})
export class GearGeneratorService {
  oneHandWeaponTypes: WeaponType[];
  allGearTypes: string;

  static cache: { [key: string]: string } = {};

  constructor(private ruleFormatter: RuleGeneratorService, private itemDataService: ItemDataService,
    private helper: GenerationHelperService
  ) {
    this.oneHandWeaponTypes = this.itemDataService.getOneHandWeaponTypes();
    this.allGearTypes = this.itemDataService.getAllGearTypes().map(g => `"${g}"`).join(' ');
  }

  public generateUnidentifiedGearRules(filter: Filter): string {
    if (poe1) return '';

    const weaponClass = this.helper.buildWeaponClass([WeaponType.All]);
    const armourClass = this.helper.buildArmourClass([ArmourType.All]);

    // TODO: min item level for unidentified gear?
    // TODO: investigate why we hit this twice on initialisation

    let rareUnidentifiedRules = '';

    if (filter.showTieredRareGear) {
      let weaponBaseTypes: string[] = [];
      let armourBaseTypes: string[] = [];

      if (filter.showTieredRareGearMinBaseTypeTier === BaseTypeTier.Basic) {
        weaponBaseTypes = ['All'];
        armourBaseTypes = ['All'];
      } else {
        weaponBaseTypes = this.helper.buildBaseTypesForTier(filter.showTieredRareGearMinBaseTypeTier, [WeaponType.All], undefined);
        armourBaseTypes = this.helper.buildBaseTypesForTier(filter.showTieredRareGearMinBaseTypeTier, undefined, [ArmourType.All]);
      }

      const rareT5Tier = this.helper.getEquipmentTier(EquipmentType.OtherGearRareT5);
      const rareT4Tier = this.helper.getEquipmentTier(EquipmentType.OtherGearRareT4);
      const rareT3Tier = this.helper.getEquipmentTier(EquipmentType.OtherGearRareT3);
      const rareT2Tier = this.helper.getEquipmentTier(EquipmentType.OtherGearRareT2);

      rareUnidentifiedRules += (rareT5Tier === rareT4Tier && filter.showTieredRareGearMinTier < 5) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherGearRareT5, filter.showTieredRareGearMinTier, filter, weaponBaseTypes, weaponClass, 5, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-rare-t5', 'T5 Rare Unidentified Weapons');
      rareUnidentifiedRules += (rareT4Tier === rareT3Tier && filter.showTieredRareGearMinTier < 4) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherGearRareT4, filter.showTieredRareGearMinTier, filter, weaponBaseTypes, weaponClass, 4, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-rare-t4', 'T4 Rare Unidentified Weapons');
      rareUnidentifiedRules += (rareT3Tier === rareT2Tier && filter.showTieredRareGearMinTier < 3) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherGearRareT3, filter.showTieredRareGearMinTier, filter, weaponBaseTypes, weaponClass, 3, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-rare-t3', 'T3 Rare Unidentified Weapons');
      rareUnidentifiedRules += this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherGearRareT2, filter.showTieredRareGearMinTier, filter, weaponBaseTypes, weaponClass, 2, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-rare-t2', 'T2 Rare Unidentified Weapons');

      rareUnidentifiedRules += (rareT5Tier === rareT4Tier && filter.showTieredRareGearMinTier < 5) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherGearRareT5, filter.showTieredRareGearMinTier, filter, armourBaseTypes, armourClass, 5, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-rare-t5', 'T5 Rare Unidentified Armour');
      rareUnidentifiedRules += (rareT4Tier === rareT3Tier && filter.showTieredRareGearMinTier < 4) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherGearRareT4, filter.showTieredRareGearMinTier, filter, armourBaseTypes, armourClass, 4, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-rare-t4', 'T4 Rare Unidentified Armour');
      rareUnidentifiedRules += (rareT3Tier === rareT2Tier && filter.showTieredRareGearMinTier < 3) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherGearRareT3, filter.showTieredRareGearMinTier, filter, armourBaseTypes, armourClass, 3, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-rare-t3', 'T3 Rare Unidentified Armour');
      rareUnidentifiedRules += (rareT3Tier === rareT2Tier && filter.showTieredRareGearMinTier < 3) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherGearRareT3, filter.showTieredRareGearMinTier, filter, armourBaseTypes, armourClass, 3, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-rare-t3', 'T3 Rare Unidentified Armour');
      rareUnidentifiedRules += this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherGearRareT2, filter.showTieredRareGearMinTier, filter, armourBaseTypes, armourClass, 2, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-rare-t2', 'T2 Rare Unidentified Armour');
    }

    let magicUnidentifiedRules = '';

    if (filter.showTieredMagicGear) {
      let weaponBaseTypes: string[] = [];
      let armourBaseTypes: string[] = [];

      if (filter.showTieredMagicGearMinBaseTypeTier === BaseTypeTier.Basic) {
        weaponBaseTypes = ['All'];
        armourBaseTypes = ['All'];
      } else {
        weaponBaseTypes = this.helper.buildBaseTypesForTier(filter.showTieredMagicGearMinBaseTypeTier, [WeaponType.All], undefined);
        armourBaseTypes = this.helper.buildBaseTypesForTier(filter.showTieredMagicGearMinBaseTypeTier, undefined, [ArmourType.All]);
      }

      const magicT5Tier = this.helper.getEquipmentTier(EquipmentType.OtherGearMagicT5);
      const magicT4Tier = this.helper.getEquipmentTier(EquipmentType.OtherGearMagicT4);
      const magicT3Tier = this.helper.getEquipmentTier(EquipmentType.OtherGearMagicT3);
      const magicT2Tier = this.helper.getEquipmentTier(EquipmentType.OtherGearMagicT2);

      magicUnidentifiedRules += (magicT5Tier === magicT4Tier && filter.showTieredMagicGearMinTier < 5) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherGearMagicT5, filter.showTieredMagicGearMinTier, filter, weaponBaseTypes, weaponClass, 5, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-magic-t5', 'T5 Magic Unidentified Weapons');
      magicUnidentifiedRules += (magicT4Tier === magicT3Tier && filter.showTieredMagicGearMinTier < 4) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherGearMagicT4, filter.showTieredMagicGearMinTier, filter, weaponBaseTypes, weaponClass, 4, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-magic-t4', 'T4 Magic Unidentified Weapons');
      magicUnidentifiedRules += (magicT3Tier === magicT2Tier && filter.showTieredMagicGearMinTier < 3) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherGearMagicT3, filter.showTieredMagicGearMinTier, filter, weaponBaseTypes, weaponClass, 3, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-magic-t3', 'T3 Magic Unidentified Weapons');
      magicUnidentifiedRules += this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherGearMagicT2, filter.showTieredMagicGearMinTier, filter, weaponBaseTypes, weaponClass, 2, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-magic-t2', 'T2 Magic Unidentified Weapons');

      magicUnidentifiedRules += (magicT5Tier === magicT4Tier && filter.showTieredMagicGearMinTier < 5) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherGearMagicT5, filter.showTieredMagicGearMinTier, filter, armourBaseTypes, armourClass, 5, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-magic-t5', 'T5 Magic Unidentified Armour');
      magicUnidentifiedRules += (magicT4Tier === magicT3Tier && filter.showTieredMagicGearMinTier < 4) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherGearMagicT4, filter.showTieredMagicGearMinTier, filter, armourBaseTypes, armourClass, 4, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-magic-t4', 'T4 Magic Unidentified Armour');
      magicUnidentifiedRules += (magicT3Tier === magicT2Tier && filter.showTieredMagicGearMinTier < 3) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherGearMagicT3, filter.showTieredMagicGearMinTier, filter, armourBaseTypes, armourClass, 3, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-magic-t3', 'T3 Magic Unidentified Armour');
      magicUnidentifiedRules += this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherGearMagicT2, filter.showTieredMagicGearMinTier, filter, armourBaseTypes, armourClass, 2, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-magic-t2', 'T2 Magic Unidentified Armour');
    }

    let lowTierRareUnidentifiedRules = '';

    if (filter.showLowTierTieredRareGear && filter.showTieredRareGearMinBaseTypeTier !== BaseTypeTier.Basic) {
      let weaponBaseTypes: string[] = [];
      let armourBaseTypes: string[] = [];

      if (filter.showTieredRareGearMinBaseTypeTier === BaseTypeTier.Advanced) {
        weaponBaseTypes = ['All'];
        armourBaseTypes = ['All'];
      } else {
        const lowerTier = this.getLowerTierName(filter.showTieredRareGearMinBaseTypeTier);
        weaponBaseTypes = this.helper.buildBaseTypesForTier(lowerTier, [WeaponType.All], undefined);
        armourBaseTypes = this.helper.buildBaseTypesForTier(lowerTier, undefined, [ArmourType.All]);
      }

      const rareT5Tier = this.helper.getEquipmentTier(EquipmentType.OtherGearLowerGearTierRareT5);
      const rareT4Tier = this.helper.getEquipmentTier(EquipmentType.OtherGearLowerGearTierRareT4);
      const rareT3Tier = this.helper.getEquipmentTier(EquipmentType.OtherGearLowerGearTierRareT3);

      lowTierRareUnidentifiedRules += (rareT5Tier === rareT4Tier && filter.showLowTierTieredRareGearMinTier < 5) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherGearLowerGearTierRareT5, filter.showLowTierTieredRareGearMinTier, filter, weaponBaseTypes, weaponClass, 5, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-low-tier-rare-t5', 'Low Gear Tier T5 Rare Unidentified Weapons');
      lowTierRareUnidentifiedRules += (rareT4Tier === rareT3Tier && filter.showLowTierTieredRareGearMinTier < 4) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherGearLowerGearTierRareT4, filter.showLowTierTieredRareGearMinTier, filter, weaponBaseTypes, weaponClass, 4, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-low-tier-rare-t4', 'Low Gear Tier T4 Rare Unidentified Weapons');
      lowTierRareUnidentifiedRules += this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherGearLowerGearTierRareT3, filter.showLowTierTieredRareGearMinTier, filter, weaponBaseTypes, weaponClass, 3, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-low-tier-rare-t3', 'Low Gear Tier T3 Rare Unidentified Weapons');

      lowTierRareUnidentifiedRules += (rareT5Tier === rareT4Tier && filter.showLowTierTieredRareGearMinTier < 5) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherGearLowerGearTierRareT5, filter.showLowTierTieredRareGearMinTier, filter, armourBaseTypes, armourClass, 5, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-low-tier-rare-t5', 'Low Gear Tier T5 Rare Unidentified Armour');
      lowTierRareUnidentifiedRules += (rareT4Tier === rareT3Tier && filter.showLowTierTieredRareGearMinTier < 4) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherGearLowerGearTierRareT4, filter.showLowTierTieredRareGearMinTier, filter, armourBaseTypes, armourClass, 4, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-low-tier-rare-t4', 'Low Gear Tier T4 Rare Unidentified Armour');
      lowTierRareUnidentifiedRules += this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherGearLowerGearTierRareT3, filter.showLowTierTieredRareGearMinTier, filter, armourBaseTypes, armourClass, 3, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-low-tier-rare-t3', 'Low Gear Tier T3 Rare Unidentified Armour');
    }

    let lowTierMagicUnidentifiedRules = '';

    if (filter.showLowTierTieredMagicGear && filter.showTieredMagicGearMinBaseTypeTier !== BaseTypeTier.Basic) {
      let weaponBaseTypes: string[] = [];
      let armourBaseTypes: string[] = [];

      if (filter.showTieredMagicGearMinBaseTypeTier === BaseTypeTier.Advanced) {
        weaponBaseTypes = ['All'];
        armourBaseTypes = ['All'];
      } else {
        const lowerTier = this.getLowerTierName(filter.showTieredMagicGearMinBaseTypeTier);
        weaponBaseTypes = this.helper.buildBaseTypesForTier(lowerTier, [WeaponType.All], undefined);
        armourBaseTypes = this.helper.buildBaseTypesForTier(lowerTier, undefined, [ArmourType.All]);
      }

      const magicT5Tier = this.helper.getEquipmentTier(EquipmentType.OtherGearLowerGearTierMagicT5);
      const magicT4Tier = this.helper.getEquipmentTier(EquipmentType.OtherGearLowerGearTierMagicT4);
      const magicT3Tier = this.helper.getEquipmentTier(EquipmentType.OtherGearLowerGearTierMagicT3);

      lowTierMagicUnidentifiedRules += (magicT5Tier === magicT4Tier && filter.showLowTierTieredMagicGearMinTier < 5) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherGearLowerGearTierMagicT5, filter.showLowTierTieredMagicGearMinTier, filter, weaponBaseTypes, weaponClass, 5, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-low-tier-magic-t5', 'Low Gear Tier T5 Magic Unidentified Weapons');
      lowTierMagicUnidentifiedRules += (magicT4Tier === magicT3Tier && filter.showLowTierTieredMagicGearMinTier < 4) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherGearLowerGearTierMagicT4, filter.showLowTierTieredMagicGearMinTier, filter, weaponBaseTypes, weaponClass, 4, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-low-tier-magic-t4', 'Low Gear Tier T4 Magic Unidentified Weapons');
      lowTierMagicUnidentifiedRules += this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherGearLowerGearTierMagicT3, filter.showLowTierTieredMagicGearMinTier, filter, weaponBaseTypes, weaponClass, 3, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-low-tier-magic-t3', 'Low Gear Tier T3 Magic Unidentified Weapons');

      lowTierMagicUnidentifiedRules += (magicT5Tier === magicT4Tier && filter.showLowTierTieredMagicGearMinTier < 5) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherGearLowerGearTierMagicT5, filter.showLowTierTieredMagicGearMinTier, filter, armourBaseTypes, armourClass, 5, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-low-tier-magic-t5', 'Low Gear Tier T5 Magic Unidentified Armour');
      lowTierMagicUnidentifiedRules += (magicT4Tier === magicT3Tier && filter.showLowTierTieredMagicGearMinTier < 4) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherGearLowerGearTierMagicT4, filter.showLowTierTieredMagicGearMinTier, filter, armourBaseTypes, armourClass, 4, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-low-tier-magic-t4', 'Low Gear Tier T4 Magic Unidentified Armour');
      lowTierMagicUnidentifiedRules += this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherGearLowerGearTierMagicT3, filter.showLowTierTieredMagicGearMinTier, filter, armourBaseTypes, armourClass, 3, undefined, undefined, undefined, undefined, 'gear-unidentified-tier-low-tier-magic-t3', 'Low Gear Tier T3 Magic Unidentified Armour');
    }
    return rareUnidentifiedRules + '\n\n' + magicUnidentifiedRules + '\n\n' + lowTierRareUnidentifiedRules + '\n\n' + lowTierMagicUnidentifiedRules;
  }

  public generateMyGearRules(filter: Filter): { highlightRules: string, showRules: string } {
    // First, try to group similar rules together in case the user created multiple filters with the same conditions
    const rules: (WeaponFilter & ArmourFilter)[] = [];
    const rulesMetadata: Array<{ type: 'weapon' | 'armour', originalIndex: number }> = [];
    let highlightRules = '';
    let showRules = '';

    filter.weaponFilters.filter(w => w.show).forEach((w, weaponIndex) => {
      const originalIndex = filter.weaponFilters.findIndex(f => f === w);
      const rule = rules.find(r => WeaponFilter.equals(r, w, ['weaponType']));
      if (rule) {
        rule.weaponType.push(...w.weaponType.filter(wt => !rule.weaponType.includes(wt)));
      } else {
        rules.push(structuredClone(w) as WeaponFilter & ArmourFilter);
        rulesMetadata.push({ type: 'weapon', originalIndex });
      }
    });

    filter.armourFilters.filter(a => a.show).forEach((a, armourIndex) => {
      const originalIndex = filter.armourFilters.findIndex(f => f === a);
      const rule = rules.find(r => ArmourFilter.equals(r, a, ['armourType']));
      if (rule) {
        rule.armourType.push(...a.armourType.filter(at => !rule.armourType.includes(at)));
      } else {
        const defenceTypeRule = rules.find(r => ArmourFilter.equals(r, a, ['defenceType']));
        if (defenceTypeRule) {
          defenceTypeRule.defenceType.push(...a.defenceType.filter(dt => !defenceTypeRule.defenceType.includes(dt)));
        } else {
          rules.push(structuredClone(a) as WeaponFilter & ArmourFilter);
          rulesMetadata.push({ type: 'armour', originalIndex });
        }
      }
    });

    rules.forEach((equipment, ruleIndex) => {
      const metadata = rulesMetadata[ruleIndex];
      const myEquipmentType = metadata?.type;
      const myEquipmentIndex = metadata?.originalIndex;
      const classes = (equipment.weaponType || []).map(wt => wt.toString()).concat(equipment.armourType || []).map(c => `"${c}"`).join(' ');
      let baseTypes: string[] = [];
      if (equipment.weaponType && equipment.weaponType.length > 0) {
        baseTypes = this.helper.resolveMyGearEmittedBaseTypes(
          equipment.baseTypeMode,
          equipment.baseTypeTier,
          equipment.specificBaseTypes,
          equipment.weaponType,
          undefined,
          undefined,
        );
      } else if (equipment.armourType && equipment.armourType.length > 0) {
        baseTypes = this.helper.resolveMyGearEmittedBaseTypes(
          equipment.baseTypeMode,
          equipment.baseTypeTier,
          equipment.specificBaseTypes,
          undefined,
          equipment.armourType,
          equipment.defenceType,
        );
      }

      if (baseTypes.length === 0) {
        return;
      }

      let lowTierBaseTypes: string[] = [];
      if (equipment.baseTypeMode !== GearBaseTypeMode.Specific
        && (equipment.highlightConfig.lowTierMagic || equipment.highlightConfig.lowTierRare)
        && equipment.baseTypeTier !== BaseTypeTier.Basic) {
        const lowerTier = this.getLowerTierName(equipment.baseTypeTier);
        if (equipment.weaponType && equipment.weaponType.length > 0) {
          lowTierBaseTypes = this.helper.buildBaseTypesForTier(lowerTier, equipment.weaponType, undefined, undefined, false);
        } else if (equipment.armourType && equipment.armourType.length > 0) {
          lowTierBaseTypes = this.helper.buildBaseTypesForTier(lowerTier, undefined, equipment.armourType, equipment.defenceType, false);
        }
      }

      if (poe2) {
        if (equipment.highlightConfig.rare) {
          const rareT5Tier = this.helper.getEquipmentTier(EquipmentType.MyGearRareT5);
          const rareT4Tier = this.helper.getEquipmentTier(EquipmentType.MyGearRareT4);
          const rareT3Tier = this.helper.getEquipmentTier(EquipmentType.MyGearRareT3);
          const rareT2Tier = this.helper.getEquipmentTier(EquipmentType.MyGearRareT2);

          const rareGearName = this.buildDescriptiveGearName(equipment, 'Gear');
          highlightRules += (rareT5Tier === rareT4Tier && equipment.highlightConfig.rareMinTier < 5) ? '' : this.formatHighlightForWeaponOrArmourFilter(equipment, Rarity.Rare, EquipmentType.MyGearRareT5, equipment.highlightConfig.rareMinTier, filter, baseTypes, classes, 5, 'gear-my-rare-t5', `Favourite T5 Rare ${rareGearName}`, myEquipmentType, myEquipmentIndex);
          highlightRules += (rareT4Tier === rareT3Tier && equipment.highlightConfig.rareMinTier < 4) ? '' : this.formatHighlightForWeaponOrArmourFilter(equipment, Rarity.Rare, EquipmentType.MyGearRareT4, equipment.highlightConfig.rareMinTier, filter, baseTypes, classes, 4, 'gear-my-rare-t4', `Favourite T4 Rare ${rareGearName}`, myEquipmentType, myEquipmentIndex);
          highlightRules += (rareT3Tier === rareT2Tier && equipment.highlightConfig.rareMinTier < 3) ? '' : this.formatHighlightForWeaponOrArmourFilter(equipment, Rarity.Rare, EquipmentType.MyGearRareT3, equipment.highlightConfig.rareMinTier, filter, baseTypes, classes, 3, 'gear-my-rare-t3', `Favourite T3 Rare ${rareGearName}`, myEquipmentType, myEquipmentIndex);
          highlightRules += this.formatHighlightForWeaponOrArmourFilter(equipment, Rarity.Rare, EquipmentType.MyGearRareT2, equipment.highlightConfig.rareMinTier, filter, baseTypes, classes, 2, 'gear-my-rare-t2', `Favourite T2 Rare ${rareGearName}`, myEquipmentType, myEquipmentIndex);
        }

        if (equipment.highlightConfig.magic) {
          const magicT5Tier = this.helper.getEquipmentTier(EquipmentType.MyGearMagicT5);
          const magicT4Tier = this.helper.getEquipmentTier(EquipmentType.MyGearMagicT4);
          const magicT3Tier = this.helper.getEquipmentTier(EquipmentType.MyGearMagicT3);
          const magicT2Tier = this.helper.getEquipmentTier(EquipmentType.MyGearMagicT2);

          const magicGearName = this.buildDescriptiveGearName(equipment, 'Gear');
          highlightRules += (magicT5Tier === magicT4Tier && equipment.highlightConfig.magicMinTier < 5) ? '' : this.formatHighlightForWeaponOrArmourFilter(equipment, Rarity.Magic, EquipmentType.MyGearMagicT5, equipment.highlightConfig.magicMinTier, filter, baseTypes, classes, 5, 'gear-my-magic-t5', `Favourite T5 Magic ${magicGearName}`, myEquipmentType, myEquipmentIndex);
          highlightRules += (magicT4Tier === magicT3Tier && equipment.highlightConfig.magicMinTier < 4) ? '' : this.formatHighlightForWeaponOrArmourFilter(equipment, Rarity.Magic, EquipmentType.MyGearMagicT4, equipment.highlightConfig.magicMinTier, filter, baseTypes, classes, 4, 'gear-my-magic-t4', `Favourite T4 Magic ${magicGearName}`, myEquipmentType, myEquipmentIndex);
          highlightRules += (magicT3Tier === magicT2Tier && equipment.highlightConfig.magicMinTier < 3) ? '' : this.formatHighlightForWeaponOrArmourFilter(equipment, Rarity.Magic, EquipmentType.MyGearMagicT3, equipment.highlightConfig.magicMinTier, filter, baseTypes, classes, 3, 'gear-my-magic-t3', `Favourite T3 Magic ${magicGearName}`, myEquipmentType, myEquipmentIndex);
          highlightRules += this.formatHighlightForWeaponOrArmourFilter(equipment, Rarity.Magic, EquipmentType.MyGearMagicT2, equipment.highlightConfig.magicMinTier, filter, baseTypes, classes, 2, 'gear-my-magic-t2', `Favourite T2 Magic ${magicGearName}`, myEquipmentType, myEquipmentIndex);
        }

        if (equipment.highlightConfig.lowTierRare) {
          const rareT5Tier = this.helper.getEquipmentTier(EquipmentType.MyGearLowerGearTierRareT5);
          const rareT4Tier = this.helper.getEquipmentTier(EquipmentType.MyGearLowerGearTierRareT4);
          const rareT3Tier = this.helper.getEquipmentTier(EquipmentType.MyGearLowerGearTierRareT3);

          const lowTierRareGearName = this.buildDescriptiveGearName(equipment, 'Lower Tier Gear');
          highlightRules += (rareT5Tier === rareT4Tier && equipment.highlightConfig.lowTierRareMinTier < 5) ? '' : this.formatHighlightForWeaponOrArmourFilter(equipment, Rarity.Rare, EquipmentType.MyGearLowerGearTierRareT5, equipment.highlightConfig.lowTierRareMinTier, filter, lowTierBaseTypes, classes, 5, 'gear-my-lower-tier-rare-t5', `Favourite T5 Rare ${lowTierRareGearName}`, myEquipmentType, myEquipmentIndex);
          highlightRules += (rareT4Tier === rareT3Tier && equipment.highlightConfig.lowTierRareMinTier < 4) ? '' : this.formatHighlightForWeaponOrArmourFilter(equipment, Rarity.Rare, EquipmentType.MyGearLowerGearTierRareT4, equipment.highlightConfig.lowTierRareMinTier, filter, lowTierBaseTypes, classes, 4, 'gear-my-lower-tier-rare-t4', `Favourite T4 Rare ${lowTierRareGearName}`, myEquipmentType, myEquipmentIndex);
          highlightRules += this.formatHighlightForWeaponOrArmourFilter(equipment, Rarity.Rare, EquipmentType.MyGearLowerGearTierRareT3, equipment.highlightConfig.lowTierRareMinTier, filter, lowTierBaseTypes, classes, 3, 'gear-my-lower-tier-rare-t3', `Favourite T3 Rare ${lowTierRareGearName}`, myEquipmentType, myEquipmentIndex);
        }

        if (equipment.highlightConfig.lowTierMagic) {
          const magicT5Tier = this.helper.getEquipmentTier(EquipmentType.MyGearLowerGearTierMagicT5);
          const magicT4Tier = this.helper.getEquipmentTier(EquipmentType.MyGearLowerGearTierMagicT4);
          const magicT3Tier = this.helper.getEquipmentTier(EquipmentType.MyGearLowerGearTierMagicT3);

          const lowTierMagicGearName = this.buildDescriptiveGearName(equipment, 'Lower Tier Gear');
          highlightRules += (magicT5Tier === magicT4Tier && equipment.highlightConfig.lowTierMagicMinTier < 5) ? '' : this.formatHighlightForWeaponOrArmourFilter(equipment, Rarity.Magic, EquipmentType.MyGearLowerGearTierMagicT5, equipment.highlightConfig.lowTierMagicMinTier, filter, lowTierBaseTypes, classes, 5, 'gear-my-lower-tier-magic-t5', `Favourite T5 Magic ${lowTierMagicGearName}`, myEquipmentType, myEquipmentIndex);
          highlightRules += (magicT4Tier === magicT3Tier && equipment.highlightConfig.lowTierMagicMinTier < 4) ? '' : this.formatHighlightForWeaponOrArmourFilter(equipment, Rarity.Magic, EquipmentType.MyGearLowerGearTierMagicT4, equipment.highlightConfig.lowTierMagicMinTier, filter, lowTierBaseTypes, classes, 4, 'gear-my-lower-tier-magic-t4', `Favourite T4 Magic ${lowTierMagicGearName}`, myEquipmentType, myEquipmentIndex);
          highlightRules += this.formatHighlightForWeaponOrArmourFilter(equipment, Rarity.Magic, EquipmentType.MyGearLowerGearTierMagicT3, equipment.highlightConfig.lowTierMagicMinTier, filter, lowTierBaseTypes, classes, 3, 'gear-my-lower-tier-magic-t3', `Favourite T3 Magic ${lowTierMagicGearName}`, myEquipmentType, myEquipmentIndex);
        }
      }

      // POE2: Exceptional Quality (Excellent/Good)
      if (poe2 && equipment.highlightConfig.excellentExceptional) {
        const exceptionalBaseTypes = equipment.weaponType && equipment.weaponType.length > 0
          ? this.helper.resolveMyGearEmittedBaseTypes(
            equipment.baseTypeMode,
            equipment.baseTypeTier,
            equipment.specificBaseTypes,
            equipment.weaponType.filter(wt => wt != WeaponType.Quivers),
            undefined,
            undefined,
          )
          : this.helper.resolveMyGearEmittedBaseTypes(
            equipment.baseTypeMode,
            equipment.baseTypeTier,
            equipment.specificBaseTypes,
            undefined,
            equipment.armourType,
            equipment.defenceType,
          );

        if (exceptionalBaseTypes.length > 0) {
          const gearName = this.buildDescriptiveGearName(equipment, 'Gear');
          highlightRules += this.ruleFormatter.generateEquipmentRule(
            EquipmentType.MyGearExcellentExceptional,
            new CustomRule({
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Normal,
              advancedConditions: Object.assign(this.buildAdvancedConditions(equipment), {
                minItemLevel: equipment.itemLevel,
                unidentifiedTier: undefined,
                corrupted: YesNoAny.No,
                minQuality: equipment.highlightConfig.excellentExceptionalMinQuality,
              }),
              baseTypes: exceptionalBaseTypes,
            }),
            filter,
            `Favourite Excellent Exceptional Quality ${gearName}`,
            { customClass: classes, customLine: this.buildCustomLine(equipment) },
            undefined,
            undefined,
            myEquipmentType,
            myEquipmentIndex
          ) + '\n\n';
        }
      }

      if (poe2 && equipment.highlightConfig.goodExceptional) {
        const exceptionalBaseTypes = equipment.weaponType && equipment.weaponType.length > 0
          ? this.helper.resolveMyGearEmittedBaseTypes(
            equipment.baseTypeMode,
            equipment.baseTypeTier,
            equipment.specificBaseTypes,
            equipment.weaponType.filter(wt => wt != WeaponType.Quivers),
            undefined,
            undefined,
          )
          : this.helper.resolveMyGearEmittedBaseTypes(
            equipment.baseTypeMode,
            equipment.baseTypeTier,
            equipment.specificBaseTypes,
            undefined,
            equipment.armourType,
            equipment.defenceType,
          );

        if (exceptionalBaseTypes.length > 0) {
          const gearName = this.buildDescriptiveGearName(equipment, 'Gear');
          highlightRules += this.ruleFormatter.generateEquipmentRule(
            EquipmentType.MyGearGoodExceptional,
            new CustomRule({
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Normal,
              advancedConditions: Object.assign(this.buildAdvancedConditions(equipment), {
                minItemLevel: equipment.itemLevel,
                unidentifiedTier: undefined,
                corrupted: YesNoAny.No,
                minQuality: equipment.highlightConfig.goodExceptionalMinQuality,
              }),
              baseTypes: exceptionalBaseTypes,
            }),
            filter,
            `Favourite Good Exceptional Quality ${gearName}`,
            { customClass: classes, customLine: this.buildCustomLine(equipment) },
            undefined,
            undefined,
            myEquipmentType,
            myEquipmentIndex
          ) + '\n\n';
        }
      }

      // POE2: Exceptional Socketed (use Excellent style if enabled, else Good)
      if (poe2 && (equipment.highlightConfig.excellentExceptional || equipment.highlightConfig.goodExceptional)) {
        const ruleId = equipment.highlightConfig.excellentExceptional
          ? 'gear-my-excellent-exceptional-socketed'
          : 'gear-my-good-exceptional-socketed';

        if (equipment.weaponType && equipment.weaponType.length > 0) {
          const weaponTypesWithTwoSockets = equipment.weaponType.filter(wt => itemTypesWithTwoSockets.includes(wt) && wt != WeaponType.Quivers);
          const weaponTypesWithOneSocket = equipment.weaponType.filter(wt => !itemTypesWithTwoSockets.includes(wt) && wt != WeaponType.Quivers);

          if (weaponTypesWithTwoSockets.length > 0) {
            const baseTypesTwo = this.helper.resolveMyGearEmittedBaseTypes(
              equipment.baseTypeMode,
              equipment.baseTypeTier,
              equipment.specificBaseTypes,
              weaponTypesWithTwoSockets,
              undefined,
              undefined,
            );
            const gearName = this.buildDescriptiveGearName({ ...equipment, weaponType: weaponTypesWithTwoSockets } as WeaponFilter & ArmourFilter, 'Gear');
            if (baseTypesTwo.length > 0) {
              highlightRules += this.ruleFormatter.generateEquipmentRule(
                EquipmentType.MyGearExcellentExceptional,
                new CustomRule({
                  rarity: Rarity.Normal,
                  rarityComparator: Comparator.Equal,
                  advancedConditions: new AdvancedConditions(Object.assign(this.buildAdvancedConditions(equipment), {
                    minItemLevel: equipment.itemLevel,
                    unidentifiedTier: undefined,
                    corrupted: YesNoAny.No,
                    minSockets: 3,
                  } as AdvancedConditions)),
                  baseTypes: baseTypesTwo,
                }),
                filter,
                equipment.highlightConfig.excellentExceptional ? `Favourite Excellent Exceptional Socketed ${gearName}` : `Favourite Good Exceptional Socketed ${gearName}`,
                { customClass: this.helper.buildWeaponClass(weaponTypesWithTwoSockets), customLine: this.buildCustomLine(equipment) },
                ruleId,
                undefined,
                myEquipmentType,
                myEquipmentIndex
              ) + '\n\n';
            }
          }

          if (weaponTypesWithOneSocket.length > 0) {
            const baseTypesOne = this.helper.resolveMyGearEmittedBaseTypes(
              equipment.baseTypeMode,
              equipment.baseTypeTier,
              equipment.specificBaseTypes,
              weaponTypesWithOneSocket,
              undefined,
              undefined,
            );
            const gearName = this.buildDescriptiveGearName({ ...equipment, weaponType: weaponTypesWithOneSocket } as WeaponFilter & ArmourFilter, 'Gear');
            if (baseTypesOne.length > 0) {
              highlightRules += this.ruleFormatter.generateEquipmentRule(
                EquipmentType.MyGearExcellentExceptional,
                new CustomRule({
                  rarity: Rarity.Normal,
                  rarityComparator: Comparator.Equal,
                  advancedConditions: new AdvancedConditions(Object.assign(this.buildAdvancedConditions(equipment), {
                    minItemLevel: equipment.itemLevel,
                    unidentifiedTier: undefined,
                    corrupted: YesNoAny.No,
                    minSockets: 2,
                  } as AdvancedConditions)),
                  baseTypes: baseTypesOne,
                }),
                filter,
                equipment.highlightConfig.excellentExceptional ? `Favourite Excellent Exceptional Socketed ${gearName}` : `Favourite Good Exceptional Socketed ${gearName}`,
                { customClass: this.helper.buildWeaponClass(weaponTypesWithOneSocket), customLine: this.buildCustomLine(equipment) },
                ruleId,
                undefined,
                myEquipmentType,
                myEquipmentIndex
              ) + '\n\n';
            }
          }
        } else if (equipment.armourType && equipment.armourType.length > 0) {
          const armourTypesWithTwoSockets = equipment.armourType.filter(wt => itemTypesWithTwoSockets.includes(wt));
          const armourTypesWithOneSocket = equipment.armourType.filter(wt => !itemTypesWithTwoSockets.includes(wt));

          if (armourTypesWithTwoSockets.length > 0) {
            const baseTypesTwo = this.helper.resolveMyGearEmittedBaseTypes(
              equipment.baseTypeMode,
              equipment.baseTypeTier,
              equipment.specificBaseTypes,
              undefined,
              armourTypesWithTwoSockets,
              equipment.defenceType,
            );
            const gearName = this.buildDescriptiveGearName({ ...equipment, armourType: armourTypesWithTwoSockets } as WeaponFilter & ArmourFilter, 'Gear');
            if (baseTypesTwo.length > 0) {
              highlightRules += this.ruleFormatter.generateEquipmentRule(
                EquipmentType.MyGearExcellentExceptional,
                new CustomRule({
                  rarity: Rarity.Normal,
                  rarityComparator: Comparator.Equal,
                  advancedConditions: new AdvancedConditions(Object.assign(this.buildAdvancedConditions(equipment), {
                    minItemLevel: equipment.itemLevel,
                    unidentifiedTier: undefined,
                    corrupted: YesNoAny.No,
                    minSockets: 3,
                  } as AdvancedConditions)),
                  baseTypes: baseTypesTwo,
                }),
                filter,
                equipment.highlightConfig.excellentExceptional ? `Favourite Excellent Exceptional Socketed ${gearName}` : `Favourite Good Exceptional Socketed ${gearName}`,
                { customClass: this.helper.buildArmourClass(armourTypesWithTwoSockets), customLine: this.buildCustomLine(equipment) },
                ruleId,
                undefined,
                myEquipmentType,
                myEquipmentIndex
              ) + '\n\n';
            }
          }

          if (armourTypesWithOneSocket.length > 0) {
            const baseTypesOne = this.helper.resolveMyGearEmittedBaseTypes(
              equipment.baseTypeMode,
              equipment.baseTypeTier,
              equipment.specificBaseTypes,
              undefined,
              armourTypesWithOneSocket,
              equipment.defenceType,
            );
            const gearName = this.buildDescriptiveGearName({ ...equipment, armourType: armourTypesWithOneSocket } as WeaponFilter & ArmourFilter, 'Gear');
            if (baseTypesOne.length > 0) {
              highlightRules += this.ruleFormatter.generateEquipmentRule(
                EquipmentType.MyGearExcellentExceptional,
                new CustomRule({
                  rarity: Rarity.Normal,
                  rarityComparator: Comparator.Equal,
                  advancedConditions: new AdvancedConditions(Object.assign(this.buildAdvancedConditions(equipment), {
                    minItemLevel: equipment.itemLevel,
                    unidentifiedTier: undefined,
                    corrupted: YesNoAny.No,
                    minSockets: 2,
                  } as AdvancedConditions)),
                  baseTypes: baseTypesOne,
                }),
                filter,
                equipment.highlightConfig.excellentExceptional ? `Favourite Excellent Exceptional Socketed ${gearName}` : `Favourite Good Exceptional Socketed ${gearName}`,
                { customClass: this.helper.buildArmourClass(armourTypesWithOneSocket), customLine: this.buildCustomLine(equipment) },
                ruleId,
                undefined,
                myEquipmentType,
                myEquipmentIndex
              ) + '\n\n';
            }
          }
        }
      }

      // POE2: Item Level (Excellent/Good)
      if (poe2 && equipment.highlightConfig.excellentILevel && (equipment.itemLevel ?? 0) <= (equipment.highlightConfig.excellentILevelMin ?? 82)) {
        const iLevelGearName = this.buildDescriptiveGearName(equipment, 'Item Level Gear');
        highlightRules += this.ruleFormatter.generateEquipmentRule(
          EquipmentType.MyGearExcellentILevel,
          new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Normal,
            advancedConditions: Object.assign(this.buildAdvancedConditions(equipment), {
              minItemLevel: equipment.highlightConfig.excellentILevelMin ?? 82,
            }),
            baseTypes: baseTypes,
          }),
          filter,
          `Favourite Excellent iLevel ${iLevelGearName}`,
          { customClass: classes, customLine: this.buildCustomLine(equipment) },
          undefined,
          undefined,
          myEquipmentType,
          myEquipmentIndex
        ) + '\n\n';
      }

      if (poe2 && equipment.highlightConfig.goodILevel && (equipment.itemLevel ?? 0) <= (equipment.highlightConfig.goodILevelMin ?? 81)) {
        const iLevelGearName = this.buildDescriptiveGearName(equipment, 'Item Level Gear');
        highlightRules += this.ruleFormatter.generateEquipmentRule(
          EquipmentType.MyGearGoodILevel,
          new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Normal,
            advancedConditions: Object.assign(this.buildAdvancedConditions(equipment), {
              minItemLevel: equipment.highlightConfig.goodILevelMin ?? 81,
            }),
            baseTypes: baseTypes,
          }),
          filter,
          `Favourite Good iLevel ${iLevelGearName}`,
          { customClass: classes, customLine: this.buildCustomLine(equipment) },
          undefined,
          undefined,
          myEquipmentType,
          myEquipmentIndex
        ) + '\n\n';
      }

      // POE1: Crafting Base (Excellent/Good)
      if (poe1 && equipment.highlightConfig.excellentCraftingBase) {
        const craftingBaseGearName = this.buildDescriptiveGearName(equipment, 'Crafting Base Gear');
        highlightRules += this.ruleFormatter.generateEquipmentRule(
          EquipmentType.MyGearExcellentCraftingBase,
          new CustomRule({
            advancedConditions: Object.assign(this.buildAdvancedConditions(equipment), {
              minItemLevel: Math.max(equipment.itemLevel || 0, equipment.highlightConfig.excellentCraftingBaseMinItemLevel ?? 83),
              corrupted: YesNoAny.No,
              minQuality: equipment.highlightConfig.excellentCraftingBaseMinQuality,
            }),
            baseTypes: baseTypes,
          }),
          filter,
          `Favourite Excellent ${craftingBaseGearName}`,
          { customClass: classes, customLine: this.buildCustomLine(equipment) },
          undefined,
          undefined,
          myEquipmentType,
          myEquipmentIndex
        ) + '\n\n';
      }

      if (poe1 && equipment.highlightConfig.goodCraftingBase) {
        const craftingBaseGearName = this.buildDescriptiveGearName(equipment, 'Crafting Base Gear');
        highlightRules += this.ruleFormatter.generateEquipmentRule(
          EquipmentType.MyGearGoodCraftingBase,
          new CustomRule({
            advancedConditions: Object.assign(this.buildAdvancedConditions(equipment), {
              minItemLevel: Math.max(equipment.itemLevel || 0, equipment.highlightConfig.goodCraftingBaseMinItemLevel ?? 83),
              corrupted: YesNoAny.No,
              minQuality: equipment.highlightConfig.goodCraftingBaseMinQuality,
            }),
            baseTypes: baseTypes,
          }),
          filter,
          `Favourite Good ${craftingBaseGearName}`,
          { customClass: classes, customLine: this.buildCustomLine(equipment) },
          undefined,
          undefined,
          myEquipmentType,
          myEquipmentIndex
        ) + '\n\n';
      }

      // POE1: Memory Strands (Excellent/Good)
      if (poe1 && equipment.highlightConfig.excellentMemoryStrands) {
        const memoryStrandsGearName = this.buildDescriptiveGearName(equipment, 'Memory Strands Gear');
        highlightRules += this.ruleFormatter.generateEquipmentRule(
          EquipmentType.MyGearExcellentMemoryStrands,
          new CustomRule({
            advancedConditions: Object.assign(this.buildAdvancedConditions(equipment), {
              minItemLevel: equipment.itemLevel,
              memoryStrands: equipment.highlightConfig.excellentMemoryStrandsMinStrands ?? 1,
            }),
            baseTypes: baseTypes,
          }),
          filter,
          `Favourite Excellent ${memoryStrandsGearName}`,
          { customClass: classes, customLine: this.buildCustomLine(equipment) },
          undefined,
          undefined,
          myEquipmentType,
          myEquipmentIndex
        ) + '\n\n';
      }

      if (poe1 && equipment.highlightConfig.goodMemoryStrands) {
        const memoryStrandsGearName = this.buildDescriptiveGearName(equipment, 'Memory Strands Gear');
        highlightRules += this.ruleFormatter.generateEquipmentRule(
          EquipmentType.MyGearGoodMemoryStrands,
          new CustomRule({
            advancedConditions: Object.assign(this.buildAdvancedConditions(equipment), {
              minItemLevel: equipment.itemLevel,
              memoryStrands: equipment.highlightConfig.goodMemoryStrandsMinStrands ?? 1,
            }),
            baseTypes: baseTypes,
          }),
          filter,
          `Favourite Good ${memoryStrandsGearName}`,
          { customClass: classes, customLine: this.buildCustomLine(equipment) },
          undefined,
          undefined,
          myEquipmentType,
          myEquipmentIndex
        ) + '\n\n';
      }

      // POE1: Links (Six/Five)
      if (poe1 && equipment.highlightConfig.sixLink) {
        const linkGearName = this.buildDescriptiveGearName(equipment, 'Link Gear');
        highlightRules += this.ruleFormatter.generateEquipmentRule(
          EquipmentType.MyGear6Link,
          new CustomRule({
            advancedConditions: Object.assign(this.buildAdvancedConditions(equipment), {
              minItemLevel: equipment.itemLevel,
              corrupted: YesNoAny.No,
              minLinks: 6,
            }),
            baseTypes: baseTypes,
          }),
          filter,
          `Favourite Six ${linkGearName}`,
          { customClass: classes, customLine: this.buildCustomLine(equipment) },
          undefined,
          undefined,
          myEquipmentType,
          myEquipmentIndex
        ) + '\n\n';
      }

      if (poe1 && equipment.highlightConfig.fiveLink) {
        const linkGearName = this.buildDescriptiveGearName(equipment, 'Link Gear');
        highlightRules += this.ruleFormatter.generateEquipmentRule(
          EquipmentType.MyGear5Link,
          new CustomRule({
            advancedConditions: Object.assign(this.buildAdvancedConditions(equipment), {
              minItemLevel: equipment.itemLevel,
              corrupted: YesNoAny.No,
              minLinks: 5,
            }),
            baseTypes: baseTypes,
          }),
          filter,
          `Favourite Five ${linkGearName}`,
          { customClass: classes, customLine: this.buildCustomLine(equipment) },
          undefined,
          undefined,
          myEquipmentType,
          myEquipmentIndex
        ) + '\n\n';
      }

      if (poe1 && equipment.highlightConfig.colouredSockets && equipment.highlightConfig.colouredSocketsColours) {
        const colouredGearName = this.buildDescriptiveGearName(equipment, 'Coloured Socket Gear');
        highlightRules += this.ruleFormatter.generateEquipmentRule(
          EquipmentType.MyGearColouredSockets,
          new CustomRule({
            advancedConditions: Object.assign(this.buildAdvancedConditions(equipment), {
              minItemLevel: equipment.itemLevel,
              corrupted: YesNoAny.No,
              sockets: equipment.highlightConfig.colouredSocketsColours,
            }),
            baseTypes: baseTypes,
          }),
          filter,
          `Favourite ${colouredGearName}`,
          { customClass: classes, customLine: this.buildCustomLine(equipment) },
          undefined,
          undefined,
          myEquipmentType,
          myEquipmentIndex
        ) + '\n\n';
      }

      if (equipment.highlightConfig.regularRare) {
        const regularRareGearName = this.buildDescriptiveGearName(equipment, 'Gear');
        showRules += this.ruleFormatter.generateEquipmentRule(
          EquipmentType.MyGearRegularRare,
          new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Rare,
            baseTypes: baseTypes,
            advancedConditions: Object.assign(this.buildAdvancedConditions(equipment), { minItemLevel: equipment.itemLevel, unidentifiedTier: equipment.unidentifiedTier, minLinks: equipment.minLinks, identified: YesNoAny.No })
          }),
          filter,
          `Favourite Regular Rare ${regularRareGearName}`,
          { customClass: classes, customLine: this.buildCustomLine(equipment) },
          undefined,
          undefined,
          myEquipmentType,
          myEquipmentIndex
        ) + '\n\n';
      }

      if (equipment.highlightConfig.regularMagic && (equipment.rarity === Rarity.Magic || equipment.rarity === Rarity.Normal)) {
        const regularMagicGearName = this.buildDescriptiveGearName(equipment, 'Gear');
        showRules += this.ruleFormatter.generateEquipmentRule(
          EquipmentType.MyGearRegularMagic,
          new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Magic,
            baseTypes: baseTypes,
            advancedConditions: Object.assign(this.buildAdvancedConditions(equipment), { minItemLevel: equipment.itemLevel, unidentifiedTier: equipment.unidentifiedTier, minLinks: equipment.minLinks, identified: YesNoAny.No })
          }),
          filter,
          `Favourite Regular Magic ${regularMagicGearName}`,
          { customClass: classes, customLine: this.buildCustomLine(equipment) },
          undefined,
          undefined,
          myEquipmentType,
          myEquipmentIndex
        ) + '\n\n';
      }

      if (equipment.highlightConfig.regularNormal && equipment.rarity === Rarity.Normal) {
        const regularNormalGearName = this.buildDescriptiveGearName(equipment, 'Gear');
        showRules += this.ruleFormatter.generateEquipmentRule(
          EquipmentType.MyGearRegularNormal,
          new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Normal,
            baseTypes: baseTypes,
            advancedConditions: Object.assign(this.buildAdvancedConditions(equipment), { minItemLevel: equipment.itemLevel, unidentifiedTier: equipment.unidentifiedTier, minLinks: equipment.minLinks })
          }),
          filter,
          `Favourite Regular Normal ${regularNormalGearName}`,
          { customClass: classes, customLine: this.buildCustomLine(equipment) },
          undefined,
          undefined,
          myEquipmentType,
          myEquipmentIndex
        ) + '\n\n';
      }
    });

    return { highlightRules, showRules };
  }

  private getLowerTierName(baseTypeTier: BaseTypeTier): BaseTypeTier {
    switch (baseTypeTier) {
      case BaseTypeTier.Master:
        return BaseTypeTier.Elite;
      case BaseTypeTier.Elite:
        return BaseTypeTier.Expert;
      case BaseTypeTier.Expert:
        return BaseTypeTier.Advanced;
      default:
        return BaseTypeTier.Basic;
    }
  }

  private buildDescriptiveGearName(equipment: WeaponFilter & ArmourFilter, suffix: string = 'Gear'): string {
    const weaponTypes = equipment.weaponType?.filter(wt => wt !== WeaponType.All) || [];
    const armourTypes = equipment.armourType?.filter(at => at !== ArmourType.All) || [];

    const parts: string[] = [];

    if (weaponTypes.length > 0) {
      if (weaponTypes.length === 1) {
        parts.push(weaponTypes[0].toString());
      } else {
        parts.push(weaponTypes.map(wt => wt.toString()).join(' '));
      }
    } else if (armourTypes.length > 0) {
      if (armourTypes.length === 1) {
        parts.push(armourTypes[0].toString());
      } else {
        parts.push(armourTypes.map(at => at.toString()).join(' '));
      }
    } else {
      // Fallback to generic suffix if no specific types
      return suffix;
    }

    return parts.join(' ');
  }

  private formatHighlightForWeaponOrArmourFilter(r: WeaponFilter & ArmourFilter, rarity: Rarity, equipmentType: EquipmentType,
    minTier: number, filter: Filter, baseTypes: string[], classes: string, tier: number, id?: RuleId, name?: string, myEquipmentType?: 'weapon' | 'armour' | 'jewellery' | 'jewel' | 'flask', myEquipmentIndex?: number) {

    return this.ruleFormatter.formatHighlightForTieredEquipment(rarity, equipmentType, minTier, filter, baseTypes, classes, tier,
      r.itemLevel, r.minLinks, this.buildAdvancedConditions(r), this.buildCustomLine(r), id, name, myEquipmentType, myEquipmentIndex);
  }

  private buildCustomLine(a: AdvancedEquipmentConditions): string | undefined {
    if (!a.veiledOfSyndicateMembers) return undefined;

    let customLine = '';

    // Add syndicate members filter if specified
    if (a.veiledOfSyndicateMembers && a.veiledOfSyndicateMembers.length > 0) {
      if (a.veiledOfSyndicateMembers.includes('All Members')) {
        customLine += `\n  HasExplicitMod >=1 ${this.itemDataService.getAllSyndicateMembers()
          .filter(m => m.name !== 'All' && m.name !== 'All Members')
          .map(m => `"${m.modName || m.name}"`).join(' ')}`;
      } else if (!a.veiledOfSyndicateMembers.includes('All Veiled')) {
        const syndicateMembersString = a.veiledOfSyndicateMembers.map(member => `"${member}"`).join(' ');
        customLine += `\n  HasExplicitMod >=1 ${syndicateMembersString}`;
      }
    }

    return customLine;
  }

  private buildAdvancedConditions(a: AdvancedEquipmentConditions): AdvancedConditions {
    const advancedConditions = new AdvancedConditions();

    advancedConditions.corrupted = a.corrupted;
    advancedConditions.corruptedImplicits = a.corruptedImplicits;
    advancedConditions.fractured = a.fractured;
    advancedConditions.veiled = a.veiled;
    advancedConditions.hasInfluence = a.hasInfluence;
    advancedConditions.influences = a.influences;
    advancedConditions.memoryStrands = a.minMemoryStrands;
    advancedConditions.minBaseDefencePercentile = a.minBaseDefencePercentile;
    advancedConditions.minQuality = a.minQuality;

    return advancedConditions;
  }

  public generateInfluencedGearRules(filter: Filter): string {
    let rules = '';

    if (filter.showInfluencedGear) {
      const baseTypes = filter.showInfluencedGearMinBaseTypeTier === BaseTypeTier.Basic ? [] :
        this.helper.buildBaseTypesForTier(filter.showInfluencedGearMinBaseTypeTier, [WeaponType.All], [ArmourType.All]);

      const advancedConditions = new AdvancedConditions({
        hasInfluence: true,
        influences: ['All'],
        minItemLevel: filter.showInfluencedGearMinItemLevel || undefined,
      });

      rules += this.ruleFormatter.generateEquipmentRule(
        EquipmentType.OtherGearInfluenced,
        new CustomRule({
          filterItemClass: this.allGearTypes,
          advancedConditions,
          baseTypes,
        }),
        filter,
        'Influenced Gear'
      ) + '\n\n';
    }

    if (filter.showFracturedGear) {
      const baseTypes = filter.showFracturedGearMinBaseTypeTier === BaseTypeTier.Basic ? [] :
        this.helper.buildBaseTypesForTier(filter.showFracturedGearMinBaseTypeTier, [WeaponType.All], [ArmourType.All]);

      const advancedConditions = new AdvancedConditions({
        fractured: true,
        minItemLevel: filter.showFracturedGearMinItemLevel || undefined,
      });

      rules += this.ruleFormatter.generateEquipmentRule(
        EquipmentType.OtherGearFractured,
        new CustomRule({
          filterItemClass: this.allGearTypes,
          advancedConditions,
          baseTypes,
        }),
        filter,
        'Fractured Gear'
      ) + '\n\n';
    }

    if (filter.showMemoryStrandGear) {
      const baseTypes = filter.showMemoryStrandGearMinBaseTypeTier === BaseTypeTier.Basic ? [] :
        this.helper.buildBaseTypesForTier(filter.showMemoryStrandGearMinBaseTypeTier, [WeaponType.All], [ArmourType.All]);

      const advancedConditions = new AdvancedConditions({
        memoryStrands: filter.showMemoryStrandGearMinStrands || 1,
        minItemLevel: filter.showMemoryStrandGearMinItemLevel || undefined,
      });

      rules += this.ruleFormatter.generateEquipmentRule(
        EquipmentType.OtherGearMemoryStrands,
        new CustomRule({
          filterItemClass: this.allGearTypes,
          advancedConditions,
          baseTypes,
        }),
        filter,
        'Memory Strand Gear'
      ) + '\n\n';
    }

    if (filter.showVeiledGear) {
      const baseTypes = filter.showVeiledGearMinBaseTypeTier === BaseTypeTier.Basic ? [] :
        this.helper.buildBaseTypesForTier(filter.showVeiledGearMinBaseTypeTier, [WeaponType.All], [ArmourType.All]);

      let customLine = '';

      // Add syndicate members filter if specified
      if (filter.showVeiledGearOfSyndicateMembers && filter.showVeiledGearOfSyndicateMembers.length > 0) {
        if (filter.showVeiledGearOfSyndicateMembers.includes('All Members')) {
          customLine += `\n  HasExplicitMod >=1 ${this.itemDataService.getAllSyndicateMembers()
            .filter(m => m.name !== 'All Veiled' && m.name !== 'All Members')
            .map(m => `"${m.modName || m.name}"`).join(' ')}`;
        } else if (!filter.showVeiledGearOfSyndicateMembers.includes('All Veiled')) {
          const syndicateMembersString = filter.showVeiledGearOfSyndicateMembers.map(member => `"${member}"`).join(' ');
          customLine += `\n  HasExplicitMod >=1 ${syndicateMembersString}`;
        }
      }

      const advancedConditions = new AdvancedConditions({
        veiled: true,
        minItemLevel: filter.showVeiledGearMinItemLevel || undefined,
      });

      rules += this.ruleFormatter.generateEquipmentRule(
        EquipmentType.OtherGearVeiled,
        new CustomRule({
          advancedConditions,
          baseTypes,
        }),
        filter,
        'Veiled Gear',
        { customLine }
      ) + '\n\n';
    }

    return rules;
  }
}
